// docs/116 CI1: the inn's crowd, drawn from the visit's seed. Three to eight guests (one or two more after
// dark), each with a townsfolk frame, a name for the hover plate, a temper for how chatty they are, and
// a place: a free seat at one of the tables (never the long table, which is kept for a band's meal, nor
// a villager's stool), else a place at the bar, by the fire or by a window. The same seed, room and
// villagers always give the same crowd, so two players in the same inn see the same guests.
import { rng, type InnPlan, type Room, type Station } from '../../layout';
import { SEAT_GAP, tableSeats } from '../../seats';
import type { Pt } from '../../walk';

/** Oryx townsfolk frames (frame A; A + 18 is the off beat). */
export const GUEST_FRAMES = [38, 39, 40, 41, 42, 44, 49, 50, 73, 74, 75, 80, 110, 111, 112, 113] as const;
const FIRST = ['Tobin', 'Mags', 'Old Wim', 'Bessa', 'Corran', 'Nell', 'Dunstan', 'Agnes', 'Pip', 'Hobb', 'Marta', 'Ewan', 'Rosalind', 'Garrick', 'Tilda', 'Fenn', 'Brigid', 'Osric', 'Lark', 'Jory', 'Maud', 'Edric', 'Wenna', 'Cobb'];
const LAST = ['Reed', 'Applewhite', 'Thatcher', 'Mossbottom', 'Fairweather', 'Crook', 'Tallow', 'Barleycorn', 'Hobbs', 'Underhill', 'Penny', 'Gorse', 'Wick', 'Marsh', 'Cooper', 'Fletcher'];
const WHAT = ['a farmer in from the fields', 'a carter, road-dusty', 'the miller\'s wife', 'a tinker with a pack', 'a shepherd, smelling of sheep', 'a retired soldier', 'the village gossip', 'a travelling pilgrim', 'a fisherman', 'the cooper', 'a weaver', 'a young lad on his first ale', 'a goose-girl', 'the baker, floury still', 'a hedge-poet', 'a woodcutter'];

export type GuestHow = 'sit' | 'bar' | 'fire' | 'stand';
export interface Guest {
  key: string;
  name: string;
  what: string;
  frame: number;
  at: Station;
  how: GuestHow;
  /** The table they sit at (`room.tables` index). */
  table?: number;
  /** They have a mug in front of them / in hand. */
  mug: boolean;
  /** 0 quiet … 2 chatty. */
  talk: number;
}

const near = (a: Pt, b: Pt) => Math.abs(a.x - b.x) < SEAT_GAP && Math.abs(a.y - b.y) < 0.5;

/** Where a standing guest may be: the bar's ends, beside the chimney, the foot of the stairs, by the dice
 * table, and a pair chatting on the open floor in front of the long table when the floor runs deep. */
export function standSpots(room: Room, plan: InnPlan): Array<Station & { how: GuestHow }> {
  const F = room.floor;
  const out: Array<Station & { how: GuestHow }> = [];
  const bs = plan.bar.stand;
  if (bs[1]) out.push({ ...bs[1], how: 'bar', flip: false });
  if (bs[3]) out.push({ ...bs[3], how: 'bar', flip: true });
  if (room.hearth) out.push({ x: room.hearth.x - 1.15, y: F + 1.1, how: 'fire' });
  out.push({ x: plan.stairs.x - 1.6, y: F + 1.25, how: 'stand', flip: true });
  if (plan.dice.w) out.push({ x: plan.dice.x - 1.3, y: plan.dice.y + 0.85, how: 'stand' });
  const t = room.table;
  if (t && F + 4 <= room.h - 1.6) out.push({ x: t.x + t.w - 1.4, y: F + 4, how: 'stand' }, { x: t.x + t.w - 0.6, y: F + 4.05, how: 'stand', flip: true });
  return out;
}

/**
 * CI1: the crowd for a visit. `taken` are the villagers' places (their stools are not offered); `night`
 * brings one or two more in.
 */
export function crowdOf(seed: number, night: boolean, room: Room, plan: InnPlan, taken: readonly Pt[] = []): Guest[] {
  const r = rng((seed ^ 0x5eed1) >>> 0);
  const n = 3 + Math.floor(r() * 6) + (night ? 1 + Math.floor(r() * 2) : 0);
  const shuffle = <T,>(xs: T[]): T[] => { for (let i = xs.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [xs[i], xs[j]] = [xs[j]!, xs[i]!]; } return xs; };
  const seats = shuffle(tableSeats(room).filter((s) => s.table !== 0 && !taken.some((t) => near(s, t))));
  const stands = shuffle(standSpots(room, plan).filter((s) => !taken.some((t) => Math.abs(t.x - s.x) < 0.8 && Math.abs(t.y - s.y) < 0.6)));
  const frames = shuffle([...GUEST_FRAMES]);
  const firsts = shuffle([...FIRST]), lasts = shuffle([...LAST]), whats = shuffle([...WHAT]);
  // about two in three sit, where there are seats
  const sitN = Math.min(seats.length, Math.max(1, Math.round(n * (0.55 + r() * 0.2))));
  const out: Guest[] = [];
  for (let i = 0; i < n; i++) {
    const seat = i < sitN ? seats[i] : undefined;
    const stand = seat ? undefined : stands[i - sitN];
    if (!seat && !stand) break;
    const name = `${firsts[i % firsts.length]!} ${lasts[i % lasts.length]!}`;
    const base = { key: `g:${i}`, name, what: whats[i % whats.length]!, frame: frames[i % frames.length]!, mug: r() < 0.75, talk: Math.floor(r() * 3) };
    if (seat) out.push({ ...base, at: { x: seat.x, y: seat.y, seated: true, flip: r() < 0.5 }, how: 'sit', table: seat.table });
    else if (stand) { const { how, ...at } = stand; out.push({ ...base, at, how }); }
  }
  return out;
}
